import { memo, useEffect, useState } from 'react';

import {
  useTwinErrors,
  useTwinPreview,
} from '../../../../hooks/storeHooks/useTwin.ts';
import { CameraUi } from './CameraUi';
import { DefaultSelfie } from './DefaultSelfie.tsx';
import { Preview } from './Preview';
import { StyleUi } from './StyleUi.tsx';

interface SelfieTabProps {
  openNextTab: () => void;
}

const SelfieTab: React.FC<SelfieTabProps> = memo(({ openNextTab }) => {
  const {
    previewUrl,
    linkedInImageUrl,
    previewTabName,
    setPreviewTabName,
    setPreviewUrl,
  } = useTwinPreview();
  const { errors } = useTwinErrors();
  const [url, setUrl] = useState<null | string | ArrayBuffer>(previewUrl);
  const [previewType, setPreviewType] = useState<'camera' | 'upload'>(
    linkedInImageUrl ? 'upload' : 'camera',
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [previewTabName]);

  const handleSetPreview = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPreviewType('upload');
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageUrl = reader.result as string;
        setUrl(imageUrl);
        setPreviewTabName('preview');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStartCamera = () => {
    setPreviewType('camera');
    setPreviewTabName('camera');
    setUrl(null);
  };

  useEffect(() => {
    if (errors.consent === 'notMatched' && !previewTabName && previewUrl) {
      setUrl(previewUrl);
      setPreviewUrl('');
      setPreviewType('upload');
      setPreviewTabName('preview');
    }
  }, [errors.consent, previewTabName, previewUrl]);

  return (
    <>
      {(() => {
        switch (previewTabName) {
          case 'camera':
            return (
              <CameraUi
                setUrl={(url) => {
                  setUrl(url);
                }}
                setTabName={setPreviewTabName}
                setPreviewType={setPreviewType}
              />
            );
          case 'style':
            return (
              <StyleUi openNextTab={openNextTab} previewType={previewType} />
            );
          case 'preview':
            return (
              <Preview
                previewUrl={url || ''}
                setTabName={setPreviewTabName}
                previewType={previewType}
              />
            );
          default:
            return (
              <DefaultSelfie
                handleStartCamera={handleStartCamera}
                handleSetPreview={handleSetPreview}
              />
            );
        }
      })()}
    </>
  );
});

export { SelfieTab };
